// src/services/stream.ts
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../app/store";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE || "http://localhost:4000/api",
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) headers.set("authorization", `Bearer ${token}`);
    headers.set("content-type", "application/json");
    return headers;
  },
});

export interface PlayVideoRequest {
  courseId: string;
  moduleId: string;
  lessonId: string;
}

export interface PlayVideoResponse {
  signedUrl: string;
  expires: number;
  expiresIn: number;
  lessonTitle?: string;
}

export interface VerifyAccessRequest {
  courseId: string;
  moduleId: string;
  lessonId: string;
}

export interface VerifyAccessResponse {
  hasAccess: boolean;
  message: string;
}

export const streamApi = createApi({
  reducerPath: "streamApi",
  baseQuery,
  tagTypes: ["VideoAccess"],
  endpoints: (builder) => ({
    // Get signed video URL for playback
    getPlayUrl: builder.mutation<PlayVideoResponse, PlayVideoRequest>({
      query: (body) => ({
        url: "/stream/play",
        method: "POST",
        body,
      }),
      // Transform response if needed
      transformResponse: (response: PlayVideoResponse) => {
        console.log(
          "[Stream API] Got signed URL, expires in:",
          response.expiresIn,
          "seconds"
        );
        return response;
      },
      // Handle errors
      transformErrorResponse: (response: any) => {
        console.error("[Stream API] Error getting signed URL:", response);
        return response.data || { error: "Failed to get video URL" };
      },
    }),

    // Verify if user has access to a lesson (optional, for pre-checks)
    verifyAccess: builder.mutation<VerifyAccessResponse, VerifyAccessRequest>({
      query: (body) => ({
        url: "/stream/verify",
        method: "POST",
        body,
      }),
      transformResponse: (response: VerifyAccessResponse) => {
        console.log(
          "[Stream API] Access verification:",
          response.hasAccess ? "granted" : "denied"
        );
        return response;
      },
    }),
  }),
});

export const { useGetPlayUrlMutation, useVerifyAccessMutation } = streamApi;

export default streamApi;
